// @ts-check
/**
 * OrganizationDetail — peace-hub-aesthetic detail surface composed over
 * peace-os edit infrastructure (Block 6.F.3 layered absorption; refreshed
 * in Block 6.F.4 with new affordance shapes).
 *
 * Block 6.F.4 changes:
 *   - Per-field SuggestEditButton instances REMOVED (6 sites; all replaced
 *     by single page-level SuggestChangeButton at page-bottom per §3.1).
 *   - LastEditedDisplay added near kind/category line per §3.8.
 *   - "Add a note" button at bottom of CM Notes section opens
 *     CmWriteNoteForm BottomSheet per §3.4.
 *   - Empty states refreshed per §3.7 microcopy.
 *
 * Q-S5 disposition (per design plan §1.3): `@ts-check` ON. JSDoc-import
 * syntax (`@type {import('...').FooProps}`) reuses the TS prop types.
 *
 * F-A: kind rendered slug-as-is. F1: no modals (BottomSheet is the
 * primitive substitute per peace-hub aesthetic). F3: no per-field save
 * buttons. F4: dotted underline always visible (refreshed sage-300 in
 * 6.F.4). F10: validation never disables save.
 */

import { useId, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { signedInName } from '@/lib/signedInName';
import {
  canArchiveProviders,
  canDirectWrite,
  canSuggest,
  canAddNotes,
  canRecommendProvider,
  canChangeDirectoryNote,
  canDeleteProviderNote,
  canMergeProviders,
  canSeeConnectedClients as canSeeConnectedClientsFor,
} from '@/lib/capabilities';
import { api } from '@/lib/api';
import { pageWriteText } from '@/lib/errorCopy';
import { mergeRefusalText } from '@/lib/mergeRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useAutoSave } from '@/hooks/useAutoSave';
import { Skeleton } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { EditableField } from '@/components/EditableField';
import { OrgSearchAutocomplete } from '@/components/OrgSearchAutocomplete';
import { requiredValidator } from '@/lib/validators';
import { categoryDisplayFromSlug } from '@/lib/uiCategoryLabels';
import { formatRelative } from '@/lib/format';
import { RecommendedBadge } from '@/components/RecommendedBadge';
import { PageShell } from '@/components/primitives/PageShell';
import { Button } from '@/components/primitives/Button';
import { OrgContactAside } from '@/components/directory/OrgContactAside';
import { A5CoverageSection, GoodToKnowChecklist } from '@/components/directory/GoodToKnow';
import { PeaceTeamNotes } from '@/components/directory/PeaceTeamNotes';
import { DeleteNoteConfirm } from '@/components/directory/DeleteNoteConfirm';
import { OrgServicesSection } from '@/components/directory/OrgServicesSection';
import { ConnectedClients } from '@/components/directory/ConnectedClients';
import { OrgDocuments } from '@/components/directory/OrgDocuments';
import { DirectoryBand, BandOrgIdentity } from '@/components/directory/DirectoryBand';
import { DirectoryRow, DirectoryRowCard, DashedAdd } from '@/components/directory/DirectoryRow';
import { ACTION_ANCHOR_ON_DARK_PRIMARY } from '@/components/directory/actionAnchors';
import { Copy, Phone, Pencil } from 'lucide-react';
import { SuggestEditForm } from '@/components/SuggestEditForm';
import { SuggestResourceForm } from '@/components/peace-hub-port/SuggestResourceForm';
import { BackToDirectory } from '@/components/BackToDirectory';
import { CmWriteNoteForm } from '@/components/peace-hub-port/CmWriteNoteForm';




export function OrganizationDetail() {
  const { tenantSlug } = useTenant();
  const { id } = useParams();

  const query = useQuery({
    queryKey: ['provider', tenantSlug, id],
    queryFn: ({ signal }) =>
      api.getProvider(tenantSlug, /** @type {string} */ (id), signal),
    enabled: Boolean(id),
  });

  // UX-13 S3 — the detail chassis is PageShell (gate parity: the page had
  // no access gate and gains none; per-affordance capability gates carry).
  // Phase 1 §2: detail pages adopt the shell at width xl, headerless — the
  // EntityHeader below is the page's header, not the shell title.
  return (
    <PageShell maxWidth="xl" testId="page-organization">
      <BackToDirectory tenantSlug={tenantSlug} fallbackView="organizations" />

      {query.isLoading && (
        <div className="mt-4 space-y-3">
          <Skeleton width="w-2/3" height="h-6" />
          <Skeleton width="w-1/3" height="h-4" />
          <Skeleton width="w-full" height="h-32" />
        </div>
      )}

      {query.error && (
        <ErrorBanner error={query.error} onRetry={() => query.refetch()} />
      )}

      {query.data && id !== undefined && (
        <ProviderView provider={query.data} tenantSlug={tenantSlug} providerId={id} />
      )}
    </PageShell>
  );
}

/**
 * @param {{
 *   provider: import('@/lib/types').ProviderDetail,
 *   tenantSlug: string,
 *   providerId: string,
 * }} props
 */
function ProviderView({ provider, tenantSlug, providerId }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Block 6.F.4 — CM-write-note BottomSheet open state. Local to the
  // ProviderView so the form is mounted only when needed.
  const [isWriteNoteOpen, setIsWriteNoteOpen] = useState(false);
  // X.6.6-R — suggest-a-change form open state (two triggers: action-row +
  // aside "See something off?"). One SuggestEditForm instance (X.5.3 flow
  // byte-identical — same component, same props as the old SuggestChangeButton).
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Phase 1.14 — note being edited (null = add mode; non-null = edit mode
  // with pre-populated body/note_type/is_anonymous from the clicked note).
  // Same `add_notes` capability gates both add and edit per §2.2.
  const [editingNote, setEditingNote] = useState(
    /** @type {import('@/lib/types').ProviderNote | null} */ (null),
  );
  // Phase 1.14.1 — note pending hard-delete confirmation (null = no
  // confirm modal shown). Delete shows on a note to a holder of
  // `delete_provider_note`, and to its author when she holds `add_notes`
  // (the server's door and gate); the confirm makes the irreversibility
  // explicit. Migration 0073 enabled the hard delete on provider_notes;
  // server-side change_log captures metadata only (no body).
  const [deletingNote, setDeletingNote] = useState(
    /** @type {import('@/lib/types').ProviderNote | null} */ (null),
  );
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(/** @type {string | null} */ (null));

  const refetchProvider = async () => {
    await queryClient.invalidateQueries({ queryKey: ['provider', tenantSlug, providerId] });
  };

  // Per-field save callbacks via useAutoSave; identical pattern to the
  // prior TS impl (Block 6.B.1 / ADR-024 D-edit-mutation-strategy).
  const saveName = useEditProviderField(tenantSlug, providerId, 'name', 'Name updated');
  const savePhone = useEditProviderField(tenantSlug, providerId, 'phone', 'Phone updated');
  const saveEmail = useEditProviderField(tenantSlug, providerId, 'email', 'Email updated');
  const saveWebsite = useEditProviderField(tenantSlug, providerId, 'website', 'Website updated');
  const saveAddress = useSaveProviderAddress(tenantSlug, providerId);
  // Phase 1.9.3 — A5 text-shape fields (Item #4 partial).
  const savePhone2 = useEditProviderField(tenantSlug, providerId, 'phone_2', 'Secondary phone updated');
  const saveFax = useEditProviderField(tenantSlug, providerId, 'fax', 'Fax updated');
  // Phase 1.9.4 — payment_types stays on the direct-PATCH path (jsonb-array
  // column on provider_organizations; Phase 1.10 doesn't touch payment_types).
  const savePaymentTypes = useEditProviderField(tenantSlug, providerId, 'payment_types', 'Payment types updated');
  // Phase 1.10 (migration 0069) — the other 4 A5 fields moved off direct
  // PATCH onto dedicated assignment-mutation endpoints. Single-value
  // highlight families (medicaid + memory_care + gender_restrictions) save
  // via useEditProviderHighlight; multi-value SDM saves via
  // useEditProviderServiceModels.
  const saveMedicaid = useEditProviderHighlight(tenantSlug, providerId, 'medicaid', 'Medicaid updated');
  const saveDementiaUnit = useEditProviderMemoryCare(tenantSlug, providerId, 'Memory Care updated');
  const saveGenderRestrictions = useEditProviderHighlight(tenantSlug, providerId, 'gender_restrictions', 'Gender Restrictions updated');
  const saveServiceDeliveryModels = useEditProviderServiceModels(tenantSlug, providerId, 'Delivery method updated');

  // Phase 1.9.4 — role-gate the inline-edit affordances. Non-direct_write
  // users see read-only Chip displays; pre-empts the PATCH-403 fallback
  // UX (user clicks, types, sees error).
  //
  // Phase 1.11 — rebased onto capability matrix. `canEdit` (inline-edit
  // affordances) gates on `canDirectWrite`. The three middle roles
  // (primary_cm / supervising_cm / admin_support) lose `direct_write`
  // and therefore lose inline-edit affordances; they keep "Suggest a
  // change" (gated on `canSuggest`) + "Add a note" (gated on `canAddNotes`).
  const { whoami, principal } = useTenant();
  const contact = useSupportContact();
  const canEdit = canDirectWrite(whoami);
  const canSuggestChange = canSuggest(whoami);
  // Directory care-web: Connected clients is a clinical cross-reference,
  // so it mounts only for a viewer who reads every client
  // (canSeeConnectedClients: every client, unless the role is fenced from
  // them), mirroring the server route's gate. The directory page itself stays open to all roles.
  const canSeeConnectedClients = canSeeConnectedClientsFor(whoami);
  // Item 3 (hotfix 2026-06-22) — "+ Add a person" affordance on the contacts
  // card. Opens SuggestResourceForm in individual-mode pre-linked to this org.
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  // The band's h1 aria-labelledby points straight at this control's own
  // id when it's mounted (see DirectoryBand's docstring), a nested
  // control's aria-label is not reachable by ordinary content recursion.
  const nameFieldId = useId();
  const canAddNote = canAddNotes(whoami);
  const canRecommend = canRecommendProvider(whoami);
  const canDelete = canDeleteProviderNote(whoami);
  // Phase 1.14.3 — Mark-as-duplicate-of affordance. Owner +
  // data_steward only. Opens the OrgSearchAutocomplete picker; on
  // pick, calls api.proposeProviderMerge and routes to the
  // MergeReview page. `navigate` is already declared above at the
  // top of ProviderView.
  const canMerge = canMergeProviders(whoami);
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  /** @type {[string | null, (msg: string | null) => void]} */
  const [mergeError, setMergeError] = useState(/** @type {string | null} */ (null));

  // Phase 1.14.1 — confirm + execute hard-delete on the note in
  // `deletingNote`. Server-side change_log emit + DELETE are
  // transactional; client side invalidates the provider-notes query
  // on success so the row drops out of the list. On error, surface
  // the message inside the modal (operator can retry or cancel).
  const onConfirmDelete = async () => {
    if (!deletingNote || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteProviderNote(tenantSlug, providerId, deletingNote.id);
      await queryClient.invalidateQueries({
        queryKey: ['provider-notes', tenantSlug, providerId],
      });
      setDeletingNote(null);
    } catch (err) {
      setDeleteError(pageWriteText(err, contact));
    } finally {
      setDeleteBusy(false);
    }
  };

  const onCancelDelete = () => {
    if (deleteBusy) return;
    setDeletingNote(null);
    setDeleteError(null);
  };

  // Phase 1.13.2 — toggle the tenant's "Recommended" curation flag.
  // Capability-gated at both the UI (canRecommend hides the button) and
  // the server (POST handler 403s for roles outside owner + data_steward).
  // In-flight state disables the button to prevent double-clicks; query
  // invalidation on success refreshes provider + the bulk-search payload
  // so the badge updates on the directory surface too.
  const [recommendBusy, setRecommendBusy] = useState(false);
  const onToggleRecommendation = async () => {
    if (recommendBusy) return;
    setRecommendBusy(true);
    try {
      await api.setProviderRecommendation(
        tenantSlug,
        providerId,
        !provider.is_recommended,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['provider', tenantSlug, providerId] }),
        queryClient.invalidateQueries({ queryKey: ['providers-for-search', tenantSlug] }),
      ]);
    } catch (err) {
      // Stay loud — the page-level ErrorBanner pattern is the operator's
      // primary surface; toast infra varies across pages. console.error
      // is the minimum visible-in-devtools shape until a unified toast
      // surface lands.
      console.error('setProviderRecommendation failed', err);
    } finally {
      setRecommendBusy(false);
    }
  };
  // Phase 1.14.3 — Mark-as-duplicate-of handler. On winner pick from
  // OrgSearchAutocomplete: build an inherit-winner field_decisions
  // shape from the current provider snapshot, POST propose-into, then
  // route to the MergeReview page with the new merge_event_id.
  /**
   * @param {{ id: string; display_name: string }} winner
   */
  const onPickMergeWinner = async (winner) => {
    if (mergeBusy) return;
    setMergeBusy(true);
    setMergeError(null);
    try {
      // Phase 1.14.3.1 — send an EMPTY main_row. Server's
      // proposeProviderMerge handler reads both records authoritatively,
      // populates winner_value / loser_value from the plan, and applies
      // smart defaults for `chosen`:
      //   * 'auto_inherit_non_null' with winner=null → 'loser'
      //   * everything else → 'winner'
      // Operator overrides per-field on the MergeReview page. The
      // earlier shape (client-supplied null winner/loser seeds) caused
      // every row to render as "✓ agree: —" since deriveStatus(null,
      // null) returns 'agree'.
      const decisions = {
        main_row: {},
        junction_decisions: [],
      };
      const result = await api.proposeProviderMerge(
        tenantSlug,
        providerId, // loser
        winner.id, // winner
        decisions,
      );
      setMergePickerOpen(false);
      navigate(
        `/t/${encodeURIComponent(tenantSlug)}/merges/${encodeURIComponent(
          result.merge_event_id,
        )}`,
      );
    } catch (err) {
      // The server answers 409 with a code for an active draft, a listing
      // already merged away or the same record; each has its own sentence.
      setMergeError(mergeRefusalText(err, contact));
    } finally {
      setMergeBusy(false);
    }
  };

  // Phase 1.7.2 — `saveNotes` removed; legacy `notes` column dropped in
  // migration 0060. Notes write goes via CmWriteNoteForm BottomSheet
  // below (versioned provider_notes path; existing 6.F.4 affordance).

  // Block 6.F.3 — full CM Notes history via listProviderNotes (separate
  // endpoint; not embedded in getProvider per design plan §1.5).
  const notesQuery = useQuery({
    queryKey: ['provider-notes', tenantSlug, providerId],
    queryFn: ({ signal }) => api.listProviderNotes(tenantSlug, providerId, signal),
  });

  const categoryDisplay = categoryDisplayFromSlug(provider.category) ?? provider.category;

  return (
    <div className="mt-4">
      {/* Forest personality band (Directory detail rebuild §1). The
          admin-mode banner is retired — admin-ness is expressed by the edit
          affordances themselves. Editors get the inline-editable name,
          wrapped so it reads on the band; everyone else gets the band's own
          name. */}
      <DirectoryBand
        kind="organization"
        identity={<BandOrgIdentity />}
        name={provider.name}
        nameSlot={
          canEdit ? (
            <span className="[&_button]:text-cream [&_button:hover]:bg-cream/10 [&_button]:focus-visible:ring-cream/60 [&_[data-saving]]:text-cream/80">
              <EditableField
                id={nameFieldId}
                value={provider.name}
                onSave={saveName}
                label="Organization name"
                validate={requiredValidator}
                canEdit={canEdit}
              />
            </span>
          ) : undefined
        }
        nameSlotId={canEdit ? nameFieldId : undefined}
        categoryLabel={categoryDisplay}
        updatedLabel={provider.updated_at ? `Updated ${formatRelative(provider.updated_at)}` : undefined}
        badge={
          provider.is_recommended ? (
            <RecommendedBadge
              tone="onDark"
              isRecommended={provider.is_recommended}
              recommendedAt={provider.recommended_at}
              recommendedByName={provider.recommended_by_display_name}
            />
          ) : undefined
        }
        actions={
          <>
            {provider.phone && (
              <a
                href={`tel:${provider.phone.replace(/[^\d+]/g, '')}`}
                className={ACTION_ANCHOR_ON_DARK_PRIMARY}
              >
                <Phone size={16} aria-hidden="true" /> Call
              </a>
            )}
            {canSuggestChange && (
              <Button
                variant="secondary-on-dark"
                onClick={() => setSuggestOpen(true)}
                data-testid="suggest-change-button"
                icon={<Pencil size={15} aria-hidden="true" />}
              >
                Suggest a change
              </Button>
            )}
          </>
        }
      />

      {/* The steward actions (Mark as recommended / Mark as duplicate) , 
          a quiet row of small bordered paper buttons below the band
          (§1). The badge moved into the band's meta line (contract
          addendum revision 4 §5 items 3, 6, 7); this row keeps only the
          toggle + merge affordance, gated on canRecommend or canMerge , 
          never on is_recommended, so a reader never sees an orphan row. */}
      {(canRecommend || canMerge) && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="organization-steward-row">
          {canRecommend && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onToggleRecommendation}
              disabled={recommendBusy}
              data-testid="recommend-toggle"
            >
              {provider.is_recommended ? 'Remove recommendation' : 'Mark as recommended'}
            </Button>
          )}
          {canMerge && !mergePickerOpen && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setMergeError(null);
                setMergePickerOpen(true);
              }}
              disabled={mergeBusy}
              data-testid="mark-as-duplicate"
              icon={<Copy size={14} aria-hidden="true" />}
            >
              Mark as duplicate of…
            </Button>
          )}
        </div>
      )}

      <div>
        {canMerge && mergePickerOpen && (
          <div className="mt-3 rounded-card border border-sage-200 bg-sand p-4">
            <div className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-sage-700">
              Which listing should stay?
            </div>
            <p className="mb-3 text-xs text-ink">
              Search and pick the record that should stay. You&apos;ll review the
              per-field choices on the next screen before anything is applied.
            </p>
            <OrgSearchAutocomplete
              onPick={onPickMergeWinner}
              onCancel={() => {
                setMergePickerOpen(false);
                setMergeError(null);
              }}
              placeholder="Search by name"
            />
            {mergeError && (
              <div role="alert" className="mt-2 text-xs text-warm-700" data-testid="org-merge-error">
                {mergeError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* X.6.6-R — two-column premium body (single column below lg) */}
      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        {/* LEFT column */}
        <div className="flex min-w-0 flex-col gap-4">
          <PeaceTeamNotes
            notes={notesQuery.data?.rows ?? []}
            providerName={provider.name}
            onAddNote={canAddNote ? () => setIsWriteNoteOpen(true) : null}
            onEditNote={
              canDelete || canAddNote
                ? (note) => {
                    setEditingNote(note);
                    setIsWriteNoteOpen(true);
                  }
                : null
            }
            onDeleteNote={
              canDelete || canAddNote
                ? (note) => {
                    setDeletingNote(note);
                    setDeleteError(null);
                  }
                : null
            }
            canDeleteNote={(note) => canChangeDirectoryNote(whoami, note)}
            canEditNote={(note) => canChangeDirectoryNote(whoami, note)}
          />

          {/* Good to know — CM read-only checklist; admin gets the editable A5 primitives. */}
          {canEdit ? (
            <A5CoverageSection
              provider={provider}
              canEdit={canEdit}
              onSaveMedicaid={saveMedicaid}
              onSaveDementiaUnit={saveDementiaUnit}
              onSavePaymentTypes={savePaymentTypes}
              onSaveServiceDeliveryModels={saveServiceDeliveryModels}
              onSaveGenderRestrictions={saveGenderRestrictions}
            />
          ) : (
            <GoodToKnowChecklist provider={provider} />
          )}

          {/* Services & specialties — the Block 6.B.2 picker zone (UX-13 S3:
              extracted to directory/OrgServicesSection; composition preserved). */}
          <OrgServicesSection
            provider={provider}
            providerId={providerId}
            canEdit={canEdit}
            onMutated={refetchProvider}
          />

          {/* Documents on the card — a flyer, a rate sheet, a referral form.
              Attaching is for anyone who can suggest an edit; removing is
              the uploader's own or an archive_providers seat's. */}
          <OrgDocuments
            tenantSlug={tenantSlug}
            providerId={providerId}
            canAttach={canSuggestChange}
            canRemoveAny={canArchiveProviders(whoami)}
            currentUserId={whoami?.user_id ?? null}
          />

          {/* People at this organization — provider.individuals[] as the
              shared linked-row system (Directory detail rebuild §5). The
              "+ Add a person" affordance opens SuggestResourceForm in
              individual-mode pre-linked to this org; the new person enters
              the steward queue and auto-links on approve. */}
          <OrgContactsSection
            individuals={provider.individuals}
            tenantSlug={tenantSlug}
            onAddPerson={canSuggestChange || canEdit ? () => setAddPersonOpen(true) : null}
          />

          {/* Connected clients: the care-web reverse read (org side). Mounts
              only for canReadEveryClient (denied roles fire no fetch and see
              no section; the server gate mirrors this). */}
          {canSeeConnectedClients && (
            <ConnectedClients
              tenantSlug={tenantSlug}
              subject={{ kind: 'organization', id: providerId }}
              name={provider.name}
            />
          )}
        </div>

        {/* RIGHT sticky aside — contact card + See-something-off (UX-13 S3:
            extracted to directory/OrgContactAside; token-preserved, the
            suggest trigger now the Button primitive). */}
        <OrgContactAside
          provider={provider}
          canEdit={canEdit}
          canSuggestChange={canSuggestChange}
          onSuggestOpen={() => setSuggestOpen(true)}
          onSavePhone={savePhone}
          onSaveEmail={saveEmail}
          onSaveAddress={saveAddress}
          onSaveWebsite={saveWebsite}
          onSavePhone2={savePhone2}
          onSaveFax={saveFax}
        />
      </div>

      {/* CM-write-note BottomSheet (6.F.4 §3.4). Mount only when add-note
          affordance is enabled; defensive (the trigger is gated above).
          Phase 1.14 — when editingNote is set, pre-populate the form with
          its values + flip the BottomSheet title to "Edit a note". onClose
          clears editingNote so the next "+ Add a note" click opens fresh. */}
      {canAddNote && (
        <CmWriteNoteForm
          isOpen={isWriteNoteOpen}
          onClose={() => {
            setIsWriteNoteOpen(false);
            setEditingNote(null);
          }}
          tenantSlug={tenantSlug}
          providerId={providerId}
          providerName={provider.name}
          invalidationKey={['provider-notes', tenantSlug, providerId]}
          initialBody={editingNote?.body}
          initialNoteType={editingNote?.note_type}
          initialIsAnonymous={editingNote?.is_anonymous}
          editNoteId={editingNote?.id}
        />
      )}

      {/* Phase 1.14.1 — confirm modal for hard-delete, mounted while a
          note is queued for delete. Server-side change_log captures
          metadata only, so once the row is gone the body cannot be
          recovered. */}
      {deletingNote && (
        <DeleteNoteConfirm
          busy={deleteBusy}
          error={deleteError}
          errorTestId="org-note-delete-error"
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      )}

      {/* X.6.6-R — one SuggestEditForm for the action-row + aside "See something
          off?" triggers. X.5.3 multi-field suggest / send-for-review flow is
          byte-identical (same component + props the retired SuggestChangeButton
          passed). Gated on `canSuggest` — mso_staff loses it. */}
      {canSuggestChange && (
        <SuggestEditForm
          isOpen={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          tenantSlug={tenantSlug}
          targetType="organization"
          target={provider}
          invalidationKey={['provider', tenantSlug, providerId]}
        />
      )}

      {/* Item 3 (hotfix 2026-06-22) — "+ Add a person" from the contacts card.
          SuggestResourceForm opens in individual-mode, pre-seeded with this org
          as an affiliation. Mounted ONLY while open so each open gets a fresh,
          re-seeded form (the seed applies on mount). New person enters the
          steward queue + auto-links on approve. */}
      {(canSuggestChange || canEdit) && addPersonOpen && (
        <SuggestResourceForm
          isOpen={addPersonOpen}
          onClose={() => setAddPersonOpen(false)}
          tenantSlug={tenantSlug}
          submitterDisplayName={signedInName(whoami, principal) || null}
          seedTargetType="individual"
          seedAffiliationEntries={[
            { kind: 'existing', provider_organization_id: providerId },
          ]}
        />
      )}
    </div>
  );
}





/**
 * OrgContactsSection — the people-at-this-org linked-row card (Directory
 * detail rebuild §5). Zero-item collapses to the one-line form (title ·
 * invite copy · add door) per emptiness-is-quiet; otherwise the card recipe
 * with linked person rows and the dashed "+ Add a person" door below.
 *
 * Meta line: role-at-this-org (falling back to the cross-org title) leads,
 * then phone and email — the row answers "who is this and how do I reach
 * them" without a click.
 *
 * @param {{
 *   individuals: import('@/lib/types').ProviderIndividualSummary[],
 *   tenantSlug: string,
 *   onAddPerson: (() => void) | null,
 * }} props
 */
function OrgContactsSection({ individuals, tenantSlug, onAddPerson }) {
  const hasContacts = individuals.length > 0;
  if (!hasContacts) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-1">
        <span className="font-display text-lg text-sage-800">Contacts</span>
        <span className="text-sm text-ink-soft">
          Add the first person to put a face on this listing.
        </span>
        {onAddPerson && <DashedAdd label="Add a person" onClick={onAddPerson} testId="org-add-person" />}
      </div>
    );
  }
  return (
    <DirectoryRowCard
      title="Contacts"
      count={individuals.length}
      addLabel={onAddPerson ? 'Add a person' : undefined}
      onAdd={onAddPerson ?? undefined}
      addTestId="org-add-person"
    >
      {individuals.map((ind) => (
        <DirectoryRow
          key={ind.id}
          to={`/t/${encodeURIComponent(tenantSlug)}/individuals/${encodeURIComponent(ind.id)}`}
          avatar="person"
          name={ind.full_name}
          meta={[
            ind.affiliation.role ?? ind.affiliation.title ?? ind.title,
            ind.business_phone,
            ind.email,
          ].filter(Boolean)}
        />
      ))}
    </DirectoryRowCard>
  );
}

/**
 * The address's save: PATCH the parts, then re-read the listing. Answers
 * the save's geocode outcome, which the postal dialog reports.
 *
 * @param {string} tenantSlug
 * @param {string} providerId
 * @returns {(parts: import('@/components/clientRecord/AddressesCard').AddressFormParts) => Promise<import('@/lib/clientAddressesApi').ClientAddressGeocodeOutcome>}
 */
function useSaveProviderAddress(tenantSlug, providerId) {
  const queryClient = useQueryClient();
  return async (parts) => {
    const saved = await api.updateProvider(tenantSlug, providerId, { address: parts });
    await queryClient.invalidateQueries({ queryKey: ['provider', tenantSlug, providerId] });
    return saved.geocode_outcome ?? 'not_attempted';
  };
}

/**
 * Custom hook: edit a single field on a ProviderDetail. Wraps useAutoSave
 * with the field-name + undo-label baked in. Defined at module scope so
 * the rules-of-hooks lint sees a top-level custom hook.
 *
 * Phase 1.9.4 — JSDoc broadened to a generic value type. The underlying
 * useAutoSave<TData, TValue> hook is fully generic in TValue (string /
 * boolean / string[] / null all flow through unchanged); only this
 * hook's JSDoc was constrained pre-1.9.4. The hook implementation is
 * type-agnostic: `[field]: newValue` spread accepts whatever the column
 * type is.
 *
 * @template T
 * @param {string} tenantSlug
 * @param {string} providerId
 * @param {'name'|'phone'|'email'|'website'|'address'|'notes'|'phone_2'|'fax'|'medicaid'|'dementia_unit'|'payment_types'|'service_delivery_model'|'gender_restrictions'} field
 * @param {string} undoLabel
 * @returns {(newValue: T, priorValue: T) => Promise<void>}
 */
function useEditProviderField(tenantSlug, providerId, field, undoLabel) {
  // Inner @param JSDocs are intentionally absent — TS infers TValue
  // from the @template T context; pinning to `string` here pre-1.9.4
  // narrowed the return type incorrectly for the boolean / string[]
  // fields added in 1.9.4.
  const result = useAutoSave({
    queryKey: ['provider', tenantSlug, providerId],
    mutationFn: (newValue) =>
      api.updateProvider(tenantSlug, providerId, { [field]: newValue }),
    /** @param {import('@/lib/types').ProviderDetail | undefined} prev */
    onOptimisticUpdate: (prev, newValue) =>
      prev ? { ...prev, [field]: newValue } : prev,
    undoMutationFn: (priorValue) =>
      api.updateProvider(tenantSlug, providerId, { [field]: priorValue }),
    undoLabel,
  });
  return /** @type {(newValue: T, priorValue: T) => Promise<void>} */ (result.save);
}

/**
 * Phase 1.10 — save callback for single-value highlight families
 * (`medicaid` / `gender_restrictions`). The handler PATH carries the
 * family; the request body carries `{ display_name: string | null }`.
 * Optimistic update keeps the scalar field synced on the local cache
 * so the chip rerenders immediately; the bulk wire's highlights[] +
 * the page-level invalidation pick up the structural shape change at
 * the next refetch.
 *
 * @param {string} tenantSlug
 * @param {string} providerId
 * @param {'medicaid'|'gender_restrictions'} family — family this hook is bound to
 * @param {string} undoLabel
 * @returns {(newValue: string | null, priorValue: string | null) => Promise<void>}
 */
function useEditProviderHighlight(tenantSlug, providerId, family, undoLabel) {
  // Map family to the ProviderDetail scalar field for optimistic update.
  const scalarField = family === 'medicaid' ? 'medicaid' : 'gender_restrictions';
  const result = useAutoSave({
    queryKey: ['provider', tenantSlug, providerId],
    mutationFn: (newValue) =>
      api.setProviderHighlight(tenantSlug, providerId, family, newValue),
    /** @param {import('@/lib/types').ProviderDetail | undefined} prev */
    onOptimisticUpdate: (prev, newValue) =>
      prev ? { ...prev, [scalarField]: newValue } : prev,
    undoMutationFn: (priorValue) =>
      api.setProviderHighlight(tenantSlug, providerId, family, priorValue),
    undoLabel,
  });
  return /** @type {(newValue: string | null, priorValue: string | null) => Promise<void>} */ (result.save);
}

/**
 * Phase 1.10 — save callback for the memory_care boolean family.
 * EditableBoolean's value is `boolean | null` (TRUE/NULL collapsed
 * empty per r2 disposition #1). On save, translate TRUE → set
 * 'Secure Dementia Unit' assignment; NULL → clear. The optimistic
 * update sets provider.dementia_unit accordingly.
 *
 * @param {string} tenantSlug
 * @param {string} providerId
 * @param {string} undoLabel
 * @returns {(newValue: boolean | null, priorValue: boolean | null) => Promise<void>}
 */
function useEditProviderMemoryCare(tenantSlug, providerId, undoLabel) {
  const result = useAutoSave({
    queryKey: ['provider', tenantSlug, providerId],
    mutationFn: (newValue) =>
      api.setProviderHighlight(
        tenantSlug,
        providerId,
        'memory_care',
        newValue === true ? 'Secure Dementia Unit' : null,
      ),
    /** @param {import('@/lib/types').ProviderDetail | undefined} prev */
    onOptimisticUpdate: (prev, newValue) =>
      prev ? { ...prev, dementia_unit: newValue } : prev,
    undoMutationFn: (priorValue) =>
      api.setProviderHighlight(
        tenantSlug,
        providerId,
        'memory_care',
        priorValue === true ? 'Secure Dementia Unit' : null,
      ),
    undoLabel,
  });
  return /** @type {(newValue: boolean | null, priorValue: boolean | null) => Promise<void>} */ (result.save);
}

/**
 * Phase 1.10 — save callback for multi-value service-model assignments
 * (Path A plural wire shape per r2 disposition #11). Replaces the full
 * set of assignments on each call. The EditableMultiSelect primitive
 * passes `null` when the last chip is removed; coerce to empty array
 * for the API.
 *
 * @param {string} tenantSlug
 * @param {string} providerId
 * @param {string} undoLabel
 * @returns {(newValue: string[] | null, priorValue: string[] | null) => Promise<void>}
 */
function useEditProviderServiceModels(tenantSlug, providerId, undoLabel) {
  const result = useAutoSave({
    queryKey: ['provider', tenantSlug, providerId],
    mutationFn: (newValue) =>
      api.setProviderServiceModels(tenantSlug, providerId, newValue ?? []),
    /** @param {import('@/lib/types').ProviderDetail | undefined} prev */
    onOptimisticUpdate: (prev, newValue) =>
      prev ? { ...prev, service_delivery_models: newValue ?? [] } : prev,
    undoMutationFn: (priorValue) =>
      api.setProviderServiceModels(tenantSlug, providerId, priorValue ?? []),
    undoLabel,
  });
  return /** @type {(newValue: string[] | null, priorValue: string[] | null) => Promise<void>} */ (result.save);
}
